/**
 * Quando o convite de push pode aparecer.
 *
 * Irmão de `useBadgeUnlockQueue`, e de propósito: as condições de "agora é uma
 * hora boa para um overlay" já foram decididas uma vez nesta casa, e duplicá-las
 * com outro critério é como as duas telas de busca divergiram (BUG-81).
 *
 * Cinco condições, todas obrigatórias:
 *
 *  1. **Elegível** — logado e com onboarding concluído. O wizard já é uma
 *     sequência de perguntas; enfiar a permissão do sistema no meio dela é
 *     pedir "não" por cansaço.
 *  2. **Rota segura** (`isSafeCelebrationRoute`) — allowlist. Fora dela ficam
 *     `/onboarding`, `/pro*` (checkout), `/search`, `/settings`.
 *  3. **Nenhum bloqueio ativo** (`celebrationGate`): Foco, conclusão de livro,
 *     Studio, promoção de liga, cartão de conquista.
 *  4. **Assentamento** — a tela precisa existir antes do pedido. Aqui são
 *     1500ms, não os 600ms da celebração: a conquista é a resposta a uma ação
 *     que a pessoa acabou de fazer, e este convite é o app pedindo algo. Chegar
 *     antes de a Home pintar faz o pedido parecer um pop-up de site.
 *  5. **Runtime com FCM** (`isFcmSupported`) — iOS Safari fora da PWA não tem
 *     Push API. Ali o convite certo é o de instalação, e Ajustes já explica.
 *
 * A checagem barata (permissão + dispensa) vem ANTES da cara: `isFcmSupported`
 * importa `firebase/messaging` dinamicamente, e quem já respondeu — a maioria
 * absoluta das sessões — não paga esse chunk.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  isCelebrationBlocked,
  subscribeCelebrationGate,
} from "../../gamification/utils/celebrationGate";
import { isSafeCelebrationRoute } from "../../gamification/hooks/useBadgeUnlockQueue";
import { canOfferPushPrompt } from "../utils/pushPrompt";

/** Tempo de a tela assentar antes de o app pedir alguma coisa. */
const SETTLE_MS = 1500;

export interface PushPromptGateState {
  open: boolean;
  /** Fecha para esta montagem. A persistência é do próprio sheet. */
  close: () => void;
}

export function usePushPromptGate(
  eligible: boolean,
  /**
   * Este aparelho já tem token registrado no perfil?
   *
   * Vem do `AuthContext`, que o watcher já tem em mãos — **zero read**. É a
   * prova de que a permissão concedida virou canal de verdade; sem ela,
   * `granted` sozinho esconderia para sempre o convite de quem concedeu num
   * build sem APNs (ver `pushPlatformGate.ts`).
   */
  hasRegisteredToken?: boolean,
): PushPromptGateState {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  // Uma vez respondido, não reabre nesta sessão nem que a rota mude — a
  // dispensa persistida cobre as próximas, esta ref cobre esta.
  const answered = useRef(false);

  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  const canOpen =
    eligible &&
    !open &&
    !answered.current &&
    !blocked &&
    isSafeCelebrationRoute(pathname) &&
    canOfferPushPrompt({hasRegisteredToken});

  useEffect(() => {
    if (!canOpen) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        const { isFcmSupported, getNotificationPermissionState } = await import(
          "../../../lib/firebase/messaging"
        );
        const supported = await isFcmSupported();
        // Recheca com a permissão REAL injetada: no app nativo `Notification`
        // não existe (a leitura vem do plugin, assíncrona), então é ESTE
        // recheck que dá o veredito de permissão lá — o pré-check síncrono só
        // olhou plataforma e dispensa. Na web o valor é o mesmo da API, e a
        // rechecagem também cobre quem abriu Ajustes no meio do caminho.
        const permission = await getNotificationPermissionState();
        if (
          cancelled ||
          !supported ||
          !canOfferPushPrompt({ hasRegisteredToken, permission })
        ) {
          return;
        }
        setOpen(true);
      })();
    }, SETTLE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canOpen, pathname, hasRegisteredToken]);

  const close = useCallback(() => {
    answered.current = true;
    setOpen(false);
  }, []);

  return { open, close };
}
